import { fetchWithAuth, readJsonSafe } from "../../utils/api";
import { getErrorCopy, parseApiError } from "../errors";

/**
 * Staff calls for the website chat (chariot-backend routes/admin/webChats.js): the Chats tabs and Settings ▸ Website
 * chat. Each answers the parsed body, or throws an Error whose words are fit to show — the server's own (it never
 * sends internals) or the error kit's — with `problems` for a form's fields.
 */
const BASE = "/admin/web-chat";

async function call(path, options = {}) {
  let res;
  try {
    res = await fetchWithAuth(`${BASE}${path}`, options);
  } catch {
    throw Object.assign(new Error(getErrorCopy("offline").message), { status: 0, problems: null });
  }
  const body = await readJsonSafe(res);
  if (!res.ok) throw Object.assign(new Error(parseApiError(res, body).message), { status: res.status, problems: body.problems || null });
  return body;
}

/** What to show for a failed call (the words call() put on it). */
export const failureText = (err) => (err instanceof Error && err.message) || getErrorCopy("default").message;

export const webChatsApi = {
  list: ({ scope, show = "all", before = null }) => {
    const q = new URLSearchParams({ scope, show });
    if (before) q.set("before", before);
    return call(`/chats?${q.toString()}`);
  },
  detail: (id) => call(`/chats/${encodeURIComponent(id)}`),
  markHandled: (id, note) => call(`/chats/${encodeURIComponent(id)}/handled`, { method: "POST", body: JSON.stringify({ note: note || null }) }),
  // Sent on by a person: a Resolution Center ticket, or an OSR lead (services/webChat/handOff.js).
  sendToCs: (id, note) => call(`/chats/${encodeURIComponent(id)}/send-to-cs`, { method: "POST", body: JSON.stringify({ note: note || null }) }),
  sendToOsr: (id, note) => call(`/chats/${encodeURIComponent(id)}/send-to-osr`, { method: "POST", body: JSON.stringify({ note: note || null }) }),
  settings: () => call("/settings"),
  saveSettings: (changes) => call("/settings", { method: "PUT", body: JSON.stringify(changes) }),
  connectAgent: () => call("/agent/connect", { method: "POST" }),
  // Stage 2: a person takes a chat over.
  me: () => call("/me"),
  setTaking: (taking) => call("/me", { method: "PUT", body: JSON.stringify({ taking: taking === true }) }),
  live: () => call("/live", { skipSaveIndicator: true }),
  accept: (id) => call(`/chats/${encodeURIComponent(id)}/accept`, { method: "POST" }),
  reply: (id, text) => call(`/chats/${encodeURIComponent(id)}/messages`, { method: "POST", body: JSON.stringify({ text }) }),
  endLive: (id) => call(`/chats/${encodeURIComponent(id)}/end`, { method: "POST" }),
  // A chat's life: over and handled; I am typing; an answer by email for a chat nobody has live; answers by email.
  close: (id, note) => call(`/chats/${encodeURIComponent(id)}/close`, { method: "POST", body: JSON.stringify({ note: note || null }) }),
  typing: (id) => call(`/chats/${encodeURIComponent(id)}/typing`, { method: "POST", skipSaveIndicator: true }),
  emailReply: (id, text) => call(`/chats/${encodeURIComponent(id)}/email-reply`, { method: "POST", body: JSON.stringify({ text }) }),
  emailReplies: () => call("/email-replies"),
  connectEmailReplies: () => call("/email-replies/connect", { method: "POST" }),
};

/** A chat's state in one word, in the platform's colors (utils/semanticColors.js families). */
export function chatStatus(chat) {
  if (chat.live_status === "waiting") return { label: "Wants a person", family: "orange" };
  if (chat.live_status === "person") return { label: chat.live_with ? `Live with ${chat.live_with}` : "Live with the team", family: "blue" };
  if (chat.needs_response) return { label: "Waiting on us", family: "orange" };
  if (chat.cs_ticket_id) return { label: "Sent to Customer Service", family: "blue" };
  if (chat.sent_to_osr) return { label: "Sent to OSR", family: "blue" };
  if (chat.closed) return { label: "Closed", family: "gray" };
  if (chat.booked) return { label: "Booked", family: "green" };
  if (chat.lead_id) return { label: "On the OSR tab", family: "blue" };
  if (chat.live) return { label: "Chatting now", family: "blue" };
  if (chat.handled_at) return { label: "Handled", family: "gray" };
  if (chat.outcome === "answered") return { label: "Answered", family: "gray" };
  if (chat.outcome === "none") return { label: "Said nothing", family: "gray" };
  return { label: "Started", family: "gray" };
}
